import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

/** Delay before the short description appears on hover or focus. */
export const helpHoverDelay = 600;

/**
 * Contextual "?" help. Hover or keyboard focus shows a short description after
 * a delay; activating it (click, Enter or Space) opens the detailed help in a
 * modal, optionally linking to the full guide.
 */
export function HelpTip({
  title,
  summary,
  children,
  docs,
}: {
  title: string;
  summary: string;
  children?: ReactNode;
  /** A guide URL, such as /help#validation, shown as "Open the guide". */
  docs?: string;
}) {
  const [hint, setHint] = useState(false),
    [open, setOpen] = useState(false),
    [align, setAlign] = useState<"start" | "end">("start");
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined),
    trigger = useRef<HTMLButtonElement>(null),
    dialog = useRef<HTMLDialogElement>(null);
  const hintId = useId(),
    titleId = useId();
  const show = () => {
    clearTimeout(timer.current);
    const rect = trigger.current?.getBoundingClientRect();
    setAlign(rect && rect.left > innerWidth / 2 ? "end" : "start");
    timer.current = setTimeout(() => setHint(true), helpHoverDelay);
  };
  const hide = () => {
    clearTimeout(timer.current);
    setHint(false);
  };
  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => {
    if (!open) return;
    const element = dialog.current;
    if (element && !element.open) element.showModal?.();
  }, [open]);
  const close = () => {
    setOpen(false);
    trigger.current?.focus();
  };
  return (
    <span className="help-tip">
      <button
        ref={trigger}
        type="button"
        className="help-tip-trigger"
        aria-label={`Help: ${title}`}
        aria-describedby={hint ? hintId : undefined}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onKeyDown={(event) => {
          if (event.key === "Escape") hide();
        }}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          hide();
          setOpen(true);
        }}
      >
        ?
      </button>
      {hint && (
        <span
          role="tooltip"
          id={hintId}
          className={`help-tip-hint help-tip-hint-${align}`}
        >
          {summary}
        </span>
      )}
      {open &&
        createPortal(
          <dialog
            ref={dialog}
            className="help-tip-dialog"
            aria-labelledby={titleId}
            onClose={close}
            onClick={(event) => {
              if (event.target === event.currentTarget) close();
            }}
          >
            <header>
              <h2 id={titleId}>{title}</h2>
              <button
                type="button"
                className="icon-button"
                aria-label="Close help"
                onClick={close}
              >
                ×
              </button>
            </header>
            <div className="help-tip-body">
              {children ?? <p>{summary}</p>}
              {docs && (
                <p>
                  <a href={docs} target="_blank" rel="noreferrer">
                    Open the guide
                  </a>
                </p>
              )}
            </div>
          </dialog>,
          trigger.current?.getRootNode() instanceof ShadowRoot
            ? (trigger.current.getRootNode() as unknown as HTMLElement)
            : document.body,
        )}
    </span>
  );
}
